import React, { useState, useEffect } from 'react';
import { Modal } from '../ui/Modal';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Label } from '../ui/Label';
import { Textarea } from '../ui/Textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/Select';
import { 
  Building2, 
  Users, 
  Save,
  Code
} from 'lucide-react';

const AddEditDepartmentModal = ({ isOpen, onClose, department, onSave, saving = false }) => {
  const [formData, setFormData] = useState({
    departmentName: '',
    departmentCode: '',
    description: '',
    isActive: true
  });
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (isOpen) {
      if (department) {
        // Map frontend format to backend format
        setFormData({
          departmentName: department.name || department.departmentName || '',
          departmentCode: department.code || department.departmentCode || '',
          description: department.description || '',
          isActive: department.isActive !== undefined ? department.isActive : (department.status === 'active')
        });
      } else {
        // Reset form for new department
        setFormData({
          departmentName: '',
          departmentCode: '',
          description: '',
          isActive: true
        });
      }
      setErrors({});
    }
  }, [isOpen, department]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error for this field
    if (errors[field]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };


  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.departmentName?.trim()) {
      newErrors.departmentName = 'Department name is required';
    }
    
    if (!formData.departmentCode?.trim()) {
      newErrors.departmentCode = 'Department code is required';
    } else if (formData.departmentCode.length > 50) {
      newErrors.departmentCode = 'Department code must not exceed 50 characters';
    }
    
    // OrganizationId will be auto-selected from first organization if not set
    // Description is optional, no validation needed
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    // Prepare data in backend format (DepartmentRequest DTO)
    // Note: organizationId removed - departments use tenant only
    const departmentData = {
      departmentName: formData.departmentName.trim(),
      departmentCode: formData.departmentCode.trim().toUpperCase(), // Convert to uppercase
      description: formData.description?.trim() || null,
      isActive: formData.isActive !== undefined ? formData.isActive : true
    };

    // Remove null/empty values for optional fields
    if (!departmentData.description) delete departmentData.description;

    onSave(departmentData);
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={saving ? undefined : onClose}
      title={department ? 'Edit Department' : 'Add New Department'}
      size="xl"
    >
      <div className="flex flex-col h-full max-h-[calc(90vh-120px)]">
        {/* Scrollable Content Area */}
        <div className="flex-1 overflow-y-auto p-6 scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100">
          <form onSubmit={handleSubmit} id="department-form" className="space-y-6">
          {/* Basic Information */}
          <div className="space-y-4">
            <div className="flex items-center gap-2 pb-2 border-b border-gray-200">
              <Building2 className="h-5 w-5 text-blue-600" />
              <h3 className="text-lg font-semibold text-gray-900">Basic Information</h3>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="departmentName" className="text-sm font-medium text-gray-700">
                  Department Name <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="departmentName"
                  value={formData.departmentName}
                  onChange={(e) => handleInputChange('departmentName', e.target.value)}
                  placeholder="Enter department name"
                  className={errors.departmentName ? 'border-red-500' : ''}
                  maxLength={255}
                />
                {errors.departmentName && (
                  <p className="text-sm text-red-500">{errors.departmentName}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="departmentCode" className="text-sm font-medium text-gray-700">
                  Department Code <span className="text-red-500">*</span>
                </Label>
                <div className="relative">
                  <Code className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input
                    id="departmentCode"
                    value={formData.departmentCode}
                    onChange={(e) => handleInputChange('departmentCode', e.target.value.toUpperCase())}
                    placeholder="e.g., IT, CS, SALES"
                    className={`pl-10 ${errors.departmentCode ? 'border-red-500' : ''}`}
                    maxLength={50}
                  />
                </div>
                {errors.departmentCode && (
                  <p className="text-sm text-red-500">{errors.departmentCode}</p>
                )}
                <p className="text-xs text-gray-500">Unique code for the department (max 50 characters)</p>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="description" className="text-sm font-medium text-gray-700">
                Description
              </Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => handleInputChange('description', e.target.value)}
                placeholder="Enter department description (optional)"
                rows={3}
              />
            </div>
          </div>

          </form>
        </div>
        
        {/* Action Buttons - Fixed at Bottom */}
        <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-200 bg-gray-50 flex-shrink-0">
          <Button
            type="button"
            variant="outline"
            onClick={onClose}
            className="px-6"
          >
            Cancel
          </Button>
          <Button
            type="submit"
            form="department-form"
            disabled={saving}
            className="flex items-center gap-2 px-6 bg-blue-600 hover:bg-blue-700 text-white disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Save className={`h-4 w-4 ${saving ? 'animate-spin' : ''}`} />
            {saving 
              ? (department ? 'Updating...' : 'Creating...') 
              : (department ? 'Update Department' : 'Create Department')
            }
          </Button>
        </div>
      </div>

    </Modal>
  );
};

export default AddEditDepartmentModal;
